import {
  MAX_SELECTED_SKILLS,
  SKILLS_RESOLVE_VERSION,
  type ResolvedSkillDisplay,
  type ResolvedSkillTargetFile,
  type Skill,
  type SkillDataSourceSelection,
  type SkillListResponse,
  type SkillSummary,
  type SkillsResolveExplorerContext,
  type SkillsResolveRequest,
  type SkillsResolveResponse,
  type SkillsResolveSelectorDiagnostics,
} from "./types";

type JsonObject = Record<string, unknown>;

export function isRecord(value: unknown): value is JsonObject {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function hasOwn(record: JsonObject, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isNullableStringArray(value: unknown): value is string[] | null {
  return value === null || isStringArray(value);
}

function hasValidOptionalString(record: JsonObject, key: string, nullable = false): boolean {
  if (!hasOwn(record, key)) return true;
  return nullable ? isNullableString(record[key]) : typeof record[key] === "string";
}

export function isSkillDataSourceSelection(value: unknown): value is SkillDataSourceSelection {
  if (!isRecord(value)) return false;
  if (value.mode === "all") {
    return !hasOwn(value, "dataSourceIds") || isStringArray(value.dataSourceIds);
  }
  return value.mode === "custom" && isStringArray(value.dataSourceIds);
}

export function isSkillSummary(value: unknown): value is SkillSummary {
  if (!isRecord(value)) return false;

  return (
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.slug) &&
    typeof value.description === "string" &&
    typeof value.enabled === "boolean" &&
    (value.scope === "personal" || value.scope === "team" || value.scope === "org") &&
    (value.source === "manual" || value.source === "directory") &&
    (!hasOwn(value, "origin") ||
      value.origin === "personal" ||
      value.origin === "organization" ||
      value.origin === "finster") &&
    (!hasOwn(value, "category") || isNullableString(value.category)) &&
    hasValidOptionalString(value, "updatedAt") &&
    hasValidOptionalString(value, "companyId", true) &&
    hasValidOptionalString(value, "companyName", true)
  );
}

export function isSkill(value: unknown): value is Skill {
  if (!isRecord(value) || !isSkillSummary(value)) return false;

  return (
    typeof value.instructions === "string" &&
    isSkillDataSourceSelection(value.dataSources) &&
    isStringArray(value.targetFileIds) &&
    (!hasOwn(value, "userFileIds") || isStringArray(value.userFileIds)) &&
    typeof value.createdAt === "string" &&
    typeof value.updatedAt === "string" &&
    hasValidOptionalString(value, "userId") &&
    hasValidOptionalString(value, "orgId", true) &&
    hasValidOptionalString(value, "sourceSkillId")
  );
}

export function isSkillListResponse(value: unknown): value is SkillListResponse {
  return (
    isRecord(value) &&
    Array.isArray(value.items) &&
    value.items.every((item) => isSkillSummary(item))
  );
}

export function isSkillsResolveRequest(value: unknown): value is SkillsResolveRequest {
  if (!isRecord(value)) return false;
  const allowedKeys = new Set([
    "submission_id",
    "messages",
    "skill_ids",
    "data_sources",
    "explorer_context",
  ]);
  if (Object.keys(value).some((key) => !allowedKeys.has(key))) return false;
  if (
    !isNonEmptyString(value.submission_id) ||
    !Array.isArray(value.messages) ||
    value.messages.length === 0 ||
    !value.messages.every((message) => isRecord(message))
  ) {
    return false;
  }
  if (
    hasOwn(value, "skill_ids") &&
    (!isStringArray(value.skill_ids) ||
      value.skill_ids.length > MAX_SELECTED_SKILLS ||
      value.skill_ids.some((id) => !id.trim()) ||
      new Set(value.skill_ids).size !== value.skill_ids.length)
  ) {
    return false;
  }
  if (
    hasOwn(value, "data_sources") &&
    (!isStringArray(value.data_sources) || value.data_sources.some((source) => !source.trim()))
  ) {
    return false;
  }
  if (hasOwn(value, "explorer_context")) {
    const context = value.explorer_context;
    if (!isRecord(context)) return false;
    const allowedContextKeys = new Set(["user_file_ids", "user_file_tags"]);
    if (Object.keys(context).some((key) => !allowedContextKeys.has(key))) {
      return false;
    }
    if (hasOwn(context, "user_file_ids") && !isNullableStringArray(context.user_file_ids)) {
      return false;
    }
    if (hasOwn(context, "user_file_tags") && !isNullableStringArray(context.user_file_tags)) {
      return false;
    }
  }
  return true;
}

function isResolvedSkillDisplay(value: unknown): value is ResolvedSkillDisplay {
  if (!isRecord(value)) return false;
  return (
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.slug) &&
    (value.origin === "personal" ||
      value.origin === "organization" ||
      value.origin === "finster") &&
    typeof value.description === "string" &&
    (value.data_source_mode === "all" || value.data_source_mode === "custom") &&
    isStringArray(value.data_source_ids) &&
    isStringArray(value.target_file_ids)
  );
}

function isResolvedSkillTargetFile(value: unknown): value is ResolvedSkillTargetFile {
  if (!isRecord(value)) return false;
  return (
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.file_type) &&
    isNonEmptyString(value.status)
  );
}

function isResolveExplorerContext(value: unknown): value is SkillsResolveExplorerContext {
  if (!isRecord(value)) return false;
  return (
    isNullableStringArray(value.user_file_ids) &&
    isNullableStringArray(value.user_file_tags) &&
    Array.isArray(value.skill_target_files) &&
    value.skill_target_files.every((target) => isResolvedSkillTargetFile(target))
  );
}

function isSelectorDiagnostics(value: unknown): value is SkillsResolveSelectorDiagnostics {
  if (!isRecord(value)) return false;
  return (
    typeof value.path === "string" &&
    typeof value.matched === "boolean" &&
    typeof value.fallback === "boolean" &&
    typeof value.latency_ms === "number" &&
    Number.isInteger(value.latency_ms) &&
    value.latency_ms >= 0 &&
    isNullableString(value.source)
  );
}

function hasValidGuidance(guidance: unknown, chars: unknown): boolean {
  if (typeof chars !== "number" || !Number.isInteger(chars) || chars < 0) {
    return false;
  }
  if (guidance === null) {
    return chars === 0;
  }
  if (typeof guidance !== "string") {
    return false;
  }
  const codePointCount = Array.from(guidance).length;
  return codePointCount > 0 && codePointCount === chars;
}

export function isSkillsResolveResponse(value: unknown): value is SkillsResolveResponse {
  if (!isRecord(value)) return false;
  if (
    value.version !== SKILLS_RESOLVE_VERSION ||
    typeof value.skill_policy_version !== "number" ||
    !Number.isInteger(value.skill_policy_version) ||
    value.skill_policy_version < 1 ||
    !isNonEmptyString(value.submission_id) ||
    (value.source !== "explicit" && value.source !== "inferred" && value.source !== "none") ||
    !isStringArray(value.skill_ids) ||
    value.skill_ids.length > MAX_SELECTED_SKILLS ||
    !Array.isArray(value.skills) ||
    value.skills.length !== value.skill_ids.length ||
    !value.skills.every((skill) => isResolvedSkillDisplay(skill)) ||
    typeof value.targets_stripped !== "boolean" ||
    !isNullableStringArray(value.effective_data_sources) ||
    !isResolveExplorerContext(value.effective_explorer_context) ||
    !isSelectorDiagnostics(value.selector) ||
    !hasValidGuidance(value.guidance, value.guidance_chars)
  ) {
    return false;
  }

  const seenIds = new Set<string>();
  const seenSlugs = new Set<string>();
  return value.skills.every((skill, index) => {
    if (value.skill_ids[index] !== skill.id || seenIds.has(skill.id) || seenSlugs.has(skill.slug)) {
      return false;
    }
    seenIds.add(skill.id);
    seenSlugs.add(skill.slug);
    return true;
  });
}
